import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import useNotifications from '../../hooks/useNotifications';
import ProfilePhotoUpload from '../../components/ProfilePhotoUpload/ProfilePhotoUpload';
import './ProfileDashboard.css';

// Section Components
import OverviewSection from './sections/OverviewSection';
import ArticlesSection from './sections/ArticlesSection';
import EventsSection from './sections/EventsSection';
import ReviewsSection from './sections/ReviewsSection';
import KudosSection from './sections/KudosSection';
import FlagsSection from './sections/FlagsSection';
import GoodsSection from './sections/GoodsSection';
import SettingsSection from './sections/SettingsSection';

const ProfileDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const isLoggedIn = !!user;
  const { sectionCounts, markSectionAsRead, refresh: refreshNotifications } = useNotifications(isLoggedIn);

  // Get section from URL or default to 'overview'
  const getInitialSection = () => {
    const params = new URLSearchParams(location.search);
    return params.get('section') || 'overview';
  };

  const [activeSection, setActiveSection] = useState(getInitialSection());
  const [expandedFolders, setExpandedFolders] = useState({
    articles: false,
    events: false,
    kudos: false,
  });
  const [profileData, setProfileData] = useState(null);
  const [stats, setStats] = useState({
    totalArticles: 0,
    totalDrafts: 0,
    totalReviews: 0,
    totalKudosReceived: 0,
    totalKudosGiven: 0,
    eventsAttended: 0,
    favoriteEvents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const contentRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  // Menu structure with badge keys for notifications
  const menuItems = [
    {
      id: 'overview',
      label: 'Overview',
      shortLabel: 'Overview',
      icon: 'bi-grid-1x2-fill',
      type: 'item',
    },
    {
      id: 'articles',
      label: 'My Articles',
      shortLabel: 'Articles',
      icon: 'bi-journal-text',
      type: 'folder',
      children: [
        { id: 'articles-drafts', label: 'Drafts', icon: 'bi-file-earmark-text' },
        { id: 'articles-submitted', label: 'Submitted', icon: 'bi-send', badgeKey: 'articles-submitted' },
        { id: 'articles-published', label: 'Published', icon: 'bi-check-circle', badgeKey: 'articles-published' },
        { id: 'articles-liked', label: 'Liked', icon: 'bi-heart' },
      ],
    },
    {
      id: 'events',
      label: 'My Events',
      shortLabel: 'Events',
      icon: 'bi-calendar-event',
      type: 'folder',
      children: [
        { id: 'events-created', label: 'Created', icon: 'bi-plus-circle' },
        { id: 'events-attended', label: 'Attended', icon: 'bi-calendar2-check' },
        { id: 'events-liked', label: 'Liked', icon: 'bi-heart' },
      ],
    },
    {
      id: 'reviews',
      label: 'My Reviews',
      shortLabel: 'Reviews',
      icon: 'bi-chat-square-quote',
      type: 'item',
    },
    {
      id: 'kudos',
      label: 'My Kudos',
      shortLabel: 'Kudos',
      icon: 'bi-award',
      type: 'folder',
      children: [
        { id: 'kudos-received', label: 'Received', icon: 'bi-gift', badgeKey: 'kudos-received' },
        { id: 'kudos-given', label: 'Given', icon: 'bi-send-check' },
      ],
    },
    {
      id: 'goods',
      label: 'My Goods',
      shortLabel: 'Goods',
      icon: 'bi-bag-heart',
      type: 'item',
    },
    {
      id: 'flags',
      label: 'My Flags',
      shortLabel: 'Flags',
      icon: 'bi-flag',
      type: 'item',
      badgeKey: 'flags',
    },
    {
      id: 'settings',
      label: 'Settings',
      shortLabel: 'Settings',
      icon: 'bi-gear',
      type: 'item',
    },
  ];

  // Get badge count for a menu item
  const getBadgeCount = (item) => {
    if (item.badgeKey && sectionCounts[item.badgeKey]) {
      return sectionCounts[item.badgeKey];
    }
    // For folders, sum up children badge counts
    if (item.children) {
      return item.children.reduce((sum, child) => {
        return sum + (child.badgeKey && sectionCounts[child.badgeKey] ? sectionCounts[child.badgeKey] : 0);
      }, 0);
    }
    return 0;
  };

  // Fetch user profile and stats
  const fetchProfileData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch profile
      const profileRes = await fetch(`${API_BASE}/auth/get-profile`, { headers });
      const profileJson = await profileRes.json();
      if (profileJson.success) {
        // Backend returns 'user' field, not 'data'
        setProfileData(profileJson.user);
      }

      // Fetch stats in parallel
      const [topicsRes, articlesRes, reviewsRes, favoritesRes, userEventsRes, kudosReceivedRes, kudosGivenRes, createdEventsRes, goodsRes, savedGoodsRes] = await Promise.all([
        fetch(`${API_BASE}/topics/user/my-topics`, { headers }).catch(() => ({ json: () => ({ data: {} }) })),
        fetch(`${API_BASE}/blogs/user/${user._id}`, { headers }).catch(() => ({ json: () => ({ data: [] }) })),
        fetch(`${API_BASE}/events/feedback/singlefeedback/${user._id}`, { headers }).catch(() => ({ json: () => ({ data: [] }) })),
        fetch(`${API_BASE}/blogs/favorites`, { headers }).catch(() => ({ json: () => ({ data: [] }) })),
        fetch(`${API_BASE}/events/user/my-events`, { headers }).catch(() => ({ json: () => ({ data: {} }) })),
        fetch(`${API_BASE}/user-kudos/received`, { headers }).catch(() => ({ json: () => ({ data: [] }) })),
        fetch(`${API_BASE}/user-kudos/given`, { headers }).catch(() => ({ json: () => ({ data: [] }) })),
        fetch(`${API_BASE}/events/user/my-created-events`, { headers }).catch(() => ({ json: () => ({ data: { total: 0 } }) })),
        fetch(`${API_BASE}/goods/my-goods/count`, { headers }).catch(() => ({ json: () => ({ data: { count: 0 } }) })),
        fetch(`${API_BASE}/goods/saved`, { headers }).catch(() => ({ json: () => ({ data: [] }) })),
      ]);

      const [topicsJson, articlesJson, reviewsJson, favoritesJson, userEventsJson, kudosReceivedJson, kudosGivenJson, createdEventsJson, goodsJson, savedGoodsJson] = await Promise.all([
        topicsRes.json?.() || { data: {} },
        articlesRes.json?.() || { data: [] },
        reviewsRes.json?.() || { data: [] },
        favoritesRes.json?.() || { data: [] },
        userEventsRes.json?.() || { data: {} },
        kudosReceivedRes.json?.() || { data: [] },
        kudosGivenRes.json?.() || { data: [] },
        createdEventsRes.json?.() || { data: { total: 0 } },
        goodsRes.json?.() || { data: { count: 0 } },
        savedGoodsRes.json?.() || { data: [] },
      ]);

      // Topics API returns { reservedTopics: [...], drafts: [...] }
      // Only count reservedTopics - drafts are content for those topics, not separate items
      const reservedTopics = topicsJson.data?.reservedTopics || [];
      const totalDrafts = reservedTopics.length;

      const articlesData = Array.isArray(articlesJson.data) ? articlesJson.data : [];
      const published = articlesData.filter(a => a.status === 'approved') || [];
      const submitted = articlesData.filter(a => a.status === 'pending') || [];

      // Reviews - API returns array of user's feedback
      const reviews = Array.isArray(reviewsJson.data) ? reviewsJson.data : [];

      // User events data - filter to only count past events for "attended"
      const userEventsData = userEventsJson.data || {};
      const allAttendedEvents = userEventsData.attended || [];
      const favoriteEventsList = userEventsData.favorites || [];

      // Filter to only past events for the "attended" count
      const now = new Date();
      const pastAttendedEvents = allAttendedEvents.filter(event => {
        const eventDate = new Date(event.endDateTime || event.startDateTime);
        return eventDate < now;
      });

      // Get kudos counts
      const kudosReceived = Array.isArray(kudosReceivedJson.data) ? kudosReceivedJson.data : [];
      const kudosGiven = Array.isArray(kudosGivenJson.data) ? kudosGivenJson.data : [];

      // Get created events count
      const createdEventsCount = createdEventsJson.data?.total || 0;

      // Get goods count
      const goodsCount = goodsJson.data?.count || 0;
      const savedGoodsCount = Array.isArray(savedGoodsJson.data) ? savedGoodsJson.data.length : 0;

      setStats({
        totalArticles: published.length,
        totalDrafts: totalDrafts,
        totalSubmitted: submitted.length,
        totalReviews: reviews.length,
        totalKudosReceived: kudosReceived.length,
        totalKudosGiven: kudosGiven.length,
        eventsAttended: pastAttendedEvents.length,
        favoriteEvents: favoriteEventsList.length,
        likedArticles: favoritesJson.data?.length || 0,
        createdEvents: createdEventsCount,
        totalGoods: goodsCount,
        savedGoods: savedGoodsCount,
      });

    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, API_BASE]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfileData();
  }, [user, navigate, fetchProfileData]);

  // Update URL when section changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('section') !== activeSection) {
      navigate(`/profile?section=${activeSection}`, { replace: true });
    }
  }, [activeSection, navigate, location.search]);

  // Scroll content area to top when section changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [activeSection]);

  // Mark notifications as read when entering a section
  useEffect(() => {
    if (activeSection && sectionCounts[activeSection] > 0) {
      markSectionAsRead(activeSection);
    }
  }, [activeSection, sectionCounts, markSectionAsRead]);

  // Close folder dropdowns when clicking outside the nav
  const sidebarNavRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarNavRef.current && !sidebarNavRef.current.contains(e.target)) {
        setExpandedFolders(prev => {
          const anyOpen = Object.values(prev).some(Boolean);
          if (!anyOpen) return prev;
          const next = {};
          Object.keys(prev).forEach(key => { next[key] = false; });
          return next;
        });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const isOpening = !prev[folderId];
      if (isOpening) {
        // Close all others, open this one
        const next = {};
        Object.keys(prev).forEach(key => { next[key] = false; });
        next[folderId] = true;
        return next;
      }
      return { ...prev, [folderId]: false };
    });
  };

  const closeAllFolders = () => {
    setExpandedFolders(prev => {
      const next = {};
      Object.keys(prev).forEach(key => { next[key] = false; });
      return next;
    });
  };

  const handleMenuClick = (item) => {
    if (item.type === 'folder') {
      toggleFolder(item.id);
    } else {
      closeAllFolders();
      setActiveSection(item.id);
    }
  };

  const handleChildClick = (childId) => {
    closeAllFolders();
    setActiveSection(childId);
  };

  const handlePhotoUploadComplete = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ profilePicture: imageUrl }),
      });
      const data = await res.json();
      if (data.success) {
        setProfileData(prev => ({ ...prev, profilePicture: imageUrl }));
        setUser(prev => ({ ...prev, profilePicture: imageUrl }));
      }
    } catch (error) {
      console.error('Photo update error:', error);
    }
    setShowPhotoEditor(false);
  };

  const renderSection = () => {
    const sectionBase = activeSection.split('-')[0];

    switch (sectionBase) {
      case 'overview':
        return <OverviewSection stats={stats} profileData={profileData} onNavigate={setActiveSection} navigate={navigate} onPhotoClick={() => setShowPhotoEditor(true)} />;
      case 'articles':
        return <ArticlesSection activeTab={activeSection} userId={user?._id} />;
      case 'events':
        return <EventsSection activeTab={activeSection} userId={user?._id} />;
      case 'reviews':
        return <ReviewsSection userId={user?._id} />;
      case 'kudos':
        return <KudosSection activeTab={activeSection} userId={user?._id} />;
      case 'flags':
        return <FlagsSection userId={user?._id} />;
      case 'goods':
        return <GoodsSection userId={user?._id} userDances={user?.dances || []} initialTab={activeSection === 'goods-saved' ? 'saved' : 'my'} />;
      case 'settings':
        return <SettingsSection profileData={profileData} setProfileData={setProfileData} setUser={setUser} />;
      default:
        return <OverviewSection stats={stats} profileData={profileData} onNavigate={setActiveSection} />;
    }
  };

  if (!user) return null;

  return (
    <div className="profile-dashboard">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        {/* User Info Header */}
        <div className="sidebar-user-info">
          <div className="user-avatar user-avatar--clickable" onClick={() => setShowPhotoEditor(true)} title="Change profile photo">
            {profileData?.profilePicture || profileData?.pic ? (
              <img
                src={profileData.profilePicture || profileData.pic}
                alt={profileData?.fullName || 'User'}
              />
            ) : (
              <div className="avatar-initials">
                {(profileData?.fullName || user?.fullName || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="avatar-edit-overlay">
              <i className="bi bi-camera"></i>
            </div>
          </div>
          <div className="user-details">
            <h3 className="user-name">{profileData?.fullName || user?.fullName || 'User'}</h3>
            {profileData?.danceAlias && (
              <span className="user-alias">@{profileData.danceAlias}</span>
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav" ref={sidebarNavRef}>
          {menuItems.map((item) => {
            const itemBadgeCount = getBadgeCount(item);
            return (
              <div key={item.id} className="nav-item-wrapper">
                <button
                  className={`nav-item ${activeSection === item.id || activeSection.startsWith(item.id + '-') ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                >
                  <span className="nav-icon">
                    <i className={`bi ${item.icon}`}></i>
                  </span>
                  <span className="nav-label nav-label--full">{item.label}</span>
                  <span className="nav-label nav-label--short">{item.shortLabel}</span>
                  {itemBadgeCount > 0 && (
                    <span className="menu-badge">{itemBadgeCount > 99 ? '99+' : itemBadgeCount}</span>
                  )}
                  {item.type === 'folder' && (
                    <span className={`folder-arrow ${expandedFolders[item.id] ? 'expanded' : ''}`}>
                      <i className="bi bi-chevron-right"></i>
                    </span>
                  )}
                </button>

                {/* Folder Children */}
                {item.type === 'folder' && expandedFolders[item.id] && (
                  <div className="nav-children">
                    {item.children.map((child) => {
                      const childBadgeCount = child.badgeKey && sectionCounts[child.badgeKey] ? sectionCounts[child.badgeKey] : 0;
                      return (
                        <button
                          key={child.id}
                          className={`nav-child ${activeSection === child.id ? 'active' : ''}`}
                          onClick={() => handleChildClick(child.id)}
                        >
                          <span className="nav-icon">
                            <i className={`bi ${child.icon}`}></i>
                          </span>
                          <span className="nav-label">{child.label}</span>
                          {childBadgeCount > 0 && (
                            <span className="menu-badge">{childBadgeCount > 99 ? '99+' : childBadgeCount}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sign Out Button */}
        <div className="sidebar-footer">
          <button
            className="sign-out-btn"
            onClick={() => {
              localStorage.removeItem('token');
              setUser(null);
              navigate('/');
              window.scrollTo(0, 0);
            }}
          >
            <i className="bi bi-box-arrow-left"></i>
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-content" ref={contentRef}>
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your profile...</p>
          </div>
        ) : (
          renderSection()
        )}
      </main>

      {/* Photo Editor Modal */}
      {showPhotoEditor && (
        <div className="photo-editor-modal-overlay" onClick={() => setShowPhotoEditor(false)}>
          <div className="photo-editor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="photo-editor-header">
              <h3>Update Profile Photo</h3>
              <button className="close-modal-btn" onClick={() => setShowPhotoEditor(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="photo-editor-content">
              <ProfilePhotoUpload
                value={profileData?.profilePicture || ''}
                onChange={handlePhotoUploadComplete}
                label=""
                maxSizeMB={5}
                minDimension={320}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDashboard;
